import AppButton from '@nxsys/components/AppButton';
import {
  Body,
  Countdown,
  CountdownLabel,
  CountdownRow,
  CountdownUnits,
  CountdownValue,
  Description,
  ProgressFill,
  ProgressTrack,
  StyledModal,
  Title,
  maskStyle,
} from './index.styled';

type SessionTimeoutModalProps = {
  open: boolean;
  secondsRemaining: number;
  /** Length of the warning window, for the progress bar to measure against. */
  totalSeconds: number;
  countdownLabel: string;
  unitsLabel: string;
  title: string;
  description: string;
  stayLabel: string;
  onStay: () => void;
};

/** 72 -> "1:12", 9 -> "0:09". Never negative, so a late tick cannot show "-1". */
function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  return `${minutes}:${String(safe % 60).padStart(2, '0')}`;
}

/**
 * The one-minute warning before an idle sign-out (NX-5238).
 *
 * Deliberately not built on AppConfirmationModal: that wires antd's `onCancel`
 * to its deny action, so a mis-click on the mask or a stray Escape would sign
 * the user straight out — the opposite of what the ticket asks for, since
 * dismissing the warning is itself activity. Here every route out of the dialog
 * means "stay signed in", and there is no path that ends the session by
 * accident. The session still ends on its own if the user never responds.
 */
const SessionTimeoutModal = ({
  open,
  secondsRemaining,
  totalSeconds,
  countdownLabel,
  unitsLabel,
  title,
  description,
  stayLabel,
  onStay,
}: SessionTimeoutModalProps) => (
  <StyledModal
    title={null}
    footer={null}
    closable={false}
    open={open}
    width={420}
    zIndex={1100}
    centered
    onCancel={onStay}
    maskClosable
    keyboard
    styles={{ mask: maskStyle }}
    modalRender={(node) => (
      <div
        role="alertdialog"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-description"
      >
        {node}
      </div>
    )}
  >
    <Countdown>
      <CountdownLabel>{countdownLabel}</CountdownLabel>
      {/* Hidden from assistive tech: re-announcing a figure every second
          drowns out everything else. The dialog's title and description carry
          the message, and they are announced once when it opens. */}
      <CountdownRow aria-hidden="true">
        <CountdownValue>{formatCountdown(secondsRemaining)}</CountdownValue>
        <CountdownUnits>{unitsLabel}</CountdownUnits>
      </CountdownRow>
      <ProgressTrack>
        <ProgressFill
          $fraction={totalSeconds > 0 ? secondsRemaining / totalSeconds : 0}
        />
      </ProgressTrack>
    </Countdown>

    <Body>
      <Title id="session-timeout-title">{title}</Title>
      <Description id="session-timeout-description">{description}</Description>
      <AppButton name={stayLabel} onClick={onStay} block />
    </Body>
  </StyledModal>
);

export default SessionTimeoutModal;
